// Shared playback helpers for the Virtual Announcement feature.
// Used by both the studio UI (manual play) and the AnnouncementScheduler
// (automated play), so a scheduled announcement sounds exactly like a preview.

import {getRecordingBlob} from "./AnnouncementStorageUtils";

export const containsDevanagari = (value = "") => /[ऀ-ॿ]/.test(value);

export const OPENING_SOUNDS = [
    {label: "School chime", type: "chime"},
    {label: "Attention tone", type: "attention"},
    {label: "Soft bell", type: "bell"},
    {label: "No sound", type: "none"},
];

export const VOICE_STYLES = [
    {label: "Formal", rate: 0.9, pitch: 0.95, gender: ""},
    {label: "Female friendly", rate: 1, pitch: 1.25, gender: "female"},
    {label: "Male calm", rate: 0.92, pitch: 0.75, gender: "male"},
    {label: "Child-friendly", rate: 1.08, pitch: 1.45, gender: "female"},
    {label: "Nepali female voice", rate: 0.9, pitch: 1.2, lang: "ne-NP", gender: "female"},
    {label: "Nepali male voice", rate: 0.88, pitch: 0.85, lang: "ne-NP", gender: "male"},
    {label: "Hindi voice", rate: 0.9, pitch: 1.05, lang: "hi-IN", gender: "female"},
];

// Name fragments that hint at a voice's gender across common Windows / macOS /
// Android / Google engines, including the Hindi voices used as a Nepali fallback.
export const FEMALE_VOICE_HINTS = [
    "female", "woman", "zira", "susan", "samantha", "victoria", "karen", "moira",
    "tessa", "fiona", "kalpana", "heera", "swara", "aditi", "veena", "raveena",
    "lekha", "google हिन्दी", "google नेपाली", "hindi female",
];
export const MALE_VOICE_HINTS = [
    "male", "man", "david", "mark", "george", "daniel", "alex", "fred",
    "hemant", "ravi", "prabhat", "hindi male",
];

const matchesGender = (voiceName = "", gender = "") => {
    if (!gender) {
        return false;
    }
    const name = voiceName.toLowerCase();
    const hints = gender === "male" ? MALE_VOICE_HINTS : FEMALE_VOICE_HINTS;
    return hints.some((hint) => name.includes(hint));
};

// Ordered list of language tokens to try when selecting a voice. Nepali has
// very poor device coverage, so we fall back to Hindi voices, which can still
// pronounce Devanagari text — this is what makes Nepali actually speak.
const buildLangChain = (preferredLang, text = "") => {
    const base = (preferredLang || (containsDevanagari(text) ? "ne-NP" : "en-US"))
        .toLowerCase()
        .replace("_", "-");
    const primary = base.split("-")[0];
    const chain = [base, primary];
    if (primary === "ne") {
        chain.push("hi-in", "hi");
    }
    return [...new Set(chain)];
};

// Choose the best available SpeechSynthesis voice for the given language and
// (optionally) gender, honouring an explicit voiceName when the user picked one.
export const pickVoice = (voices, {preferredLang, text, gender, voiceName} = {}) => {
    if (!Array.isArray(voices) || voices.length === 0) {
        return null;
    }
    if (voiceName) {
        const exact = voices.find((item) => item.name === voiceName);
        if (exact) {
            return exact;
        }
    }
    const chain = buildLangChain(preferredLang, text);
    for (const token of chain) {
        const matches = voices.filter((item) =>
            item.lang.toLowerCase().replace("_", "-").startsWith(token)
        );
        if (matches.length === 0) {
            continue;
        }
        if (gender) {
            const genderMatch = matches.find((item) => matchesGender(item.name, gender));
            if (genderMatch) {
                return genderMatch;
            }
        }
        return matches[0];
    }
    return null;
};

export const playTone = (type) => {
    if (type === "none" || typeof window === "undefined") {
        return Promise.resolve();
    }

    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) {
        return Promise.resolve();
    }

    const context = new AudioContext();
    const tones = {
        chime: [660, 880, 990],
        attention: [740, 740],
        bell: [520, 390, 520],
    };
    const frequencies = tones[type] || tones.chime;
    const duration = type === "attention" ? 0.18 : 0.24;

    frequencies.forEach((frequency, index) => {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        const start = context.currentTime + index * (duration + 0.04);

        oscillator.type = type === "bell" ? "sine" : "triangle";
        oscillator.frequency.setValueAtTime(frequency, start);
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.32, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start(start);
        oscillator.stop(start + duration + 0.02);
    });

    const totalDuration = frequencies.length * (duration + 0.04) * 1000;
    return new Promise((resolve) => {
        window.setTimeout(() => {
            context.close();
            resolve();
        }, totalDuration);
    });
};

// Speak a piece of text with the browser SpeechSynthesis engine.
// Returns a promise that resolves when speech ends (or rejects on error).
export const speakText = (text, options = {}) => {
    return new Promise((resolve, reject) => {
        if (!text || !text.trim()) {
            resolve();
            return;
        }

        if (typeof window === "undefined" || !window.speechSynthesis) {
            reject(new Error("Speech synthesis is not available in this browser."));
            return;
        }

        const {rate = 0.95, pitch = 1, voiceName, lang, gender, onStart, onEnd} = options;
        const synth = window.speechSynthesis;
        const utterance = new SpeechSynthesisUtterance(text);
        const voices = synth.getVoices();
        const preferredLang = lang || (containsDevanagari(text) ? "ne-NP" : "en-US");
        // Default Nepali/Devanagari announcements to a female voice unless the
        // caller explicitly asked for another gender.
        const effectiveGender = gender || (containsDevanagari(text) ? "female" : "");
        const voice = pickVoice(voices, {preferredLang, text, gender: effectiveGender, voiceName});

        utterance.lang = voice ? voice.lang : preferredLang;
        if (voice) {
            utterance.voice = voice;
        }
        utterance.rate = rate;
        utterance.pitch = pitch;
        utterance.onstart = () => onStart && onStart();
        utterance.onend = () => {
            onEnd && onEnd();
            resolve();
        };
        utterance.onerror = (event) => reject(event.error || new Error("Speech failed"));

        synth.cancel();
        synth.speak(utterance);
    });
};

// Play a stored recording by id. Returns a promise that resolves when it ends.
export const playRecordingById = async (recordingId, options = {}) => {
    const blob = await getRecordingBlob(recordingId);
    if (!blob) {
        throw new Error("Recording not found.");
    }
    return playRecordingBlob(blob, options);
};

// Play audio straight from a remote (server) URL.
export const playAudioUrl = (url, options = {}) => {
    return new Promise((resolve, reject) => {
        const audio = new Audio(url);
        const {onStart, onEnd} = options;

        audio.onplay = () => onStart && onStart(audio);
        audio.onended = () => {
            onEnd && onEnd();
            resolve();
        };
        audio.onerror = () => reject(new Error("Unable to play the audio."));

        audio.play().catch(reject);

        if (options.controlRef) {
            options.controlRef.current = audio;
        }
    });
};

export const playRecordingBlob = (blob, options = {}) => {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        const {onStart, onEnd} = options;

        const cleanup = () => URL.revokeObjectURL(url);

        audio.onplay = () => onStart && onStart(audio);
        audio.onended = () => {
            cleanup();
            onEnd && onEnd();
            resolve();
        };
        audio.onerror = () => {
            cleanup();
            reject(new Error("Unable to play the recording."));
        };

        audio.play().catch((err) => {
            cleanup();
            reject(err);
        });

        if (options.controlRef) {
            options.controlRef.current = audio;
        }
    });
};

// High-level: play a full announcement schedule (opening sound + content).
export const playAnnouncement = async (schedule, hooks = {}) => {
    if (!schedule) {
        return;
    }

    await playTone(schedule.openingSound || "none");

    // Uploaded audio: prefer the server URL, fall back to a locally stored blob.
    if (schedule.type === "audio") {
        if (schedule.url) {
            try {
                await playAudioUrl(schedule.url, hooks);
                return;
            } catch (e) {
                // Network / URL failure — try the local recording if we have one.
            }
        }
        if (schedule.recordingId) {
            await playRecordingById(schedule.recordingId, hooks);
        }
        return;
    }

    if (schedule.type === "recording" && schedule.recordingId) {
        await playRecordingById(schedule.recordingId, hooks);
        return;
    }

    if (schedule.type === "text" && schedule.text) {
        await speakText(schedule.text, {
            rate: schedule.rate,
            pitch: schedule.pitch,
            voiceName: schedule.voiceName,
            lang: schedule.lang,
            gender: schedule.gender,
            ...hooks,
        });
    }
};
